import { cleanGeoJSONForCesium } from "../geojson/CleanGeojson";
import { getGeometryTypeGeojson, matchKeyInText, insertByPriority, removeEpochSuffix, removeFileExtension, showToast, extractBaseName } from "../Utility";
import { loadGeoJSONToCesium } from "../geojson/LoadGeojson";
import Type from "../Types";
import { createUniqueId } from "../Utility";
import { loadCSVTypeAsync } from "./LoadCSV";
import { loadOsmTypeAsync } from "./LoadOSM";
import { loadOverlayTextGeoJSONToCesium } from "../geojson/LoadGeojsonOverlayText";
import { saveLayerToServerUnified } from "./layer/saveLayerToServer";
import { getEditedGeoJSON } from "../geojson/GetEditedGeojson";
import { serviceCreateOrEditProjects } from "../../api/services/project";
import { getProjects } from "../services/project/GetProject";
import { toggleLabel } from "../geojson/Label";
import { applyGeojsonLayerFilters } from "../filter/GeojsonFilter";

const clamp255 = (n) => Math.max(0, Math.min(255, n));

const shiftHexColor = (hex) => {
  const factor = 0.5;
  if (!hex || typeof hex !== "string") return hex;
  let h = hex.trim();
  if (!h.startsWith("#")) return hex;
  h = h.slice(1);

  if (h.length === 6) h = h + "ff";
  if (h.length !== 8) return hex;

  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const a = h.slice(6, 8);

  if ([r, g, b].some((v) => Number.isNaN(v))) return hex;

  const nr = clamp255(Math.round(r + (255 - r) * factor));
  const ng = clamp255(Math.round(g + (255 - g) * factor));
  const nb = clamp255(Math.round(b + (255 - b) * factor));

  const to2 = (v) => v.toString(16).padStart(2, "0");

  return `#${to2(nr)}${to2(ng)}${to2(nb)}${a}`;
};

// ✅ ADD: import toggleLabel from wherever it is in your project
// Example path (adjust to your real path):

/**
 * Load or update a GeoJSON layer:
 * - Without id: create new mapObject + datasource.
 * - With id: find existing mapObject and update its geojson + datasource.
 */
export async function loadGeojsonTypeAsync({
  geojsonData,
  fileName,
  id: passedId, // optional: when set, we update existing mapObject
  viewerRef,
  setMapObjects,
  defaultGeojsonStyle,
  basicGeojsonStyle,
  defaultOsmStyle,
  savedStyles = null,
  projectId = null,
  serverLayerId = null,
  isUrl = false,
  isEdited = false,
  zoomToDataSource = true,
  originalCameraControls,
  isLoadFromDatabase = false,
  sqlSchema,
  sqlTable,
  sqlDB,
  sqlWhere,
  hiddenGeojson,
  fullGeojson,
  bboxRectangle,
  bboxMaxId,
  loadOnlyInsideBbox = false,
  sqlGeometryField,
  loadingDataForLayers,
  projectTypeLoading = "main",
  setMapObjectsAssest = undefined,
  isDisableLoading = false,
  url = "",
  allowCleanGeojson = true,
  onLoaded = null,
  selectedProject = null,
  userData,
  pointCloudStyle,
  setProjects,
  applyColorChange = false,
}) {
  return new Promise(async (resolve, reject) => {
    try {
      const isUpdateExisting = !!passedId;
      const id = passedId || createUniqueId();

      const cleanedGeojsonData = allowCleanGeojson ? cleanGeoJSONForCesium(geojsonData) : geojsonData;
      const isBboxLoad = loadOnlyInsideBbox === true || !!bboxRectangle;
      const bboxLayerData = isBboxLoad
        ? {
            fullGeojson,
            bboxRectangle,
            bboxMaxId,
            bboxMaxFeaturePropertiesId: bboxMaxId,
            loadOnlyInsideBbox: true,
          }
        : {};

      const isOsmGeojson = geojsonData?.properties?.type === "OSM";
      const isCSVGeojson = geojsonData?.properties?.type === "CSV";

      const normalizedFileName = typeof fileName === "string" ? fileName.toLowerCase().replace(/[\s\-_]/g, "") : "";
      const isOverlayTextGeojson = normalizedFileName.includes("overlaytext");

      // OSM
      if (isOsmGeojson && projectTypeLoading === "main") {
        await loadOsmTypeAsync({
          fileFormat: "geojson",
          geojsonData: cleanedGeojsonData,
          fileName: fileName,
          viewerRef,
          setMapObjects,
          defaultOsmStyle,
          defaultGeojsonStyle,
          basicGeojsonStyle,
        });
        return resolve();
      }

      // CSV
      if (isCSVGeojson && projectTypeLoading === "main") {
        await loadCSVTypeAsync({
          text: null,
          fileName,
          viewerRef,
          setMapObjects,
          defaultGeojsonStyle,
          geojsonImport: cleanedGeojsonData,
        });
        return resolve();
      }

      // Normal GeoJSON
      const type = Type.ASSET.GEOJSON;

      // base style from filename
      let geojsonOption = {};
      for (const key in basicGeojsonStyle) {
        if (matchKeyInText(key, fileName || "")) {
          geojsonOption = basicGeojsonStyle[key];
          break;
        }
      }

      if (Object.keys(geojsonOption).length === 0 && isLoadFromDatabase) {
        let width = 3;
        if (["Point", "MultiPoint"].includes(getGeometryTypeGeojson(cleanedGeojsonData))) {
          width = 5;
        } else {
          width = 3;
        }

        geojsonOption = {
          dashPattern: 255,
          defaultStyle: "Solid",
          solidDashLength: 0,
          defaultFillColor: "#dc2de2ff",
          defaultLineWidth: width,
          defaultOutlineColor: "#dc2de2ff",
        };
      }

      // Auto-save flags (save after mapObjects insert + after Cesium callback fills data)
      const shouldAutoSave = projectTypeLoading === "main" && !isUpdateExisting && isUrl && projectId && selectedProject;
      let autoSaveDone = false;

      const updateProjectLayersOnServer = (newLayerId) => {
        try {
          const layers = selectedProject?.layers;
          let listOfLayersId = [];

          if (Array.isArray(layers)) {
            listOfLayersId = layers.map((x) => x?.id).filter(Boolean);
          } else if (layers && typeof layers === "object") {
            for (const k in layers) {
              const lid = layers[k]?.id;
              if (lid) listOfLayersId.push(lid);
            }
          }

          if (newLayerId) listOfLayersId.push(newLayerId);

          const formData = new FormData();
          formData.append(
            "data",
            JSON.stringify({
              username: userData.user.username,
              layers: [...new Set(listOfLayersId)],
            }),
          );
          formData.append("project_id", projectId);

          serviceCreateOrEditProjects(formData, userData.token)
            .then((res) => {
              if (res?.data?.status === "success") {
                getProjects({
                  userData,
                  setProjects,
                  onSuccess: () => {},
                  onError: () => {},
                });
              }
            })
            .catch((err) => {
              showToast(err.message, Type.NOTIFICATION.WARNING);
              console.error("Error", err);
            });
        } catch (e) {
          console.error("Error", e);
        }
      };

      const applyServerSaveResultToState = (layerIdFromServer, layerNameFromServer) => {
        setMapObjects((prev) =>
          prev.map((o) => {
            if (o.id !== id) return o;

            const nextFileName = removeFileExtension(layerNameFromServer) + (o.file_type || ".geojson");
            const nextObj = {
              ...o,
              file_name: nextFileName,
              is_edited: false,
              project_id: projectId,
              layer_id: layerIdFromServer,
            };

            return {
              ...nextObj,
              original_geojson: getEditedGeoJSON(nextObj),
            };
          }),
        );
      };

      const maybeAutoSaveToServer = (layerSnapshotForSave) => {
        if (!shouldAutoSave || autoSaveDone) return;
        autoSaveDone = true;

        saveLayerToServerUnified({
          layer: layerSnapshotForSave,
          userData,
          pointCloudStyle,
          description: "",
          createFileBaseName: removeEpochSuffix(removeFileExtension(layerSnapshotForSave.file_name)),
          isSendChangeset: false,
        })
          .then((res) => {
            if (res?.data?.status === "failed") {
              showToast(res.data.message, Type.NOTIFICATION.WARNING);
              return;
            }

            const layerIdFromServer = res?.data?.layer_id;
            const layerNameFromServer = res?.data?.layer_name;

            applyServerSaveResultToState(layerIdFromServer, layerNameFromServer);
            updateProjectLayersOnServer(layerIdFromServer);
          })
          .catch((err) => {
            console.error("Error", err);
          });
      };

      // ✅ ADD: helper to apply label after datasource is ready
      const maybeApplySavedLabel = (dataSource) => {
        if (!savedStyles) return;
        if (savedStyles.label_visible !== true) return;
        if (!viewerRef?.current) return;
        if (!dataSource) return;

        const keys = savedStyles.selected_tags_for_label;
        const textSize = savedStyles.label_text_size;
        const textColor = savedStyles.label_text_color;
        const textOffset = savedStyles.label_offset;
        const nextVisible = true;

        toggleLabel({
          viewer: viewerRef.current,
          dataSource,
          visible: nextVisible,
          keys,
          textSize,
          textColor,
          show: nextVisible,
          offset: textOffset,
        });
      };

      // create / update mapObject placeholder before loading
      if (projectTypeLoading === "main") {
        if (!isUpdateExisting) {
          // create new mapObject (main)
          const newMapObject = {
            id,
            name: "Geojson",
            entities: [],
            type,
            file_name: isUrl && projectId && selectedProject ? extractBaseName(fileName) : fileName,
            file_type: ".geojson",
            inner_entities: [],
            visible: isDisableLoading ? false : savedStyles?.style_invisible_default !== true,
            dataSource: null,
            label_visible: false,
            geojson: cleanedGeojsonData,
            isOverlayText: isOverlayTextGeojson,
            original_geojson: cleanedGeojsonData,
            hiddenGeojson,
            ...bboxLayerData,
            geojson_table_columns: cleanedGeojsonData?.table_columns,
            geojson_geometry_type: cleanedGeojsonData?.geometry_type,
            geojson_last_id: cleanedGeojsonData?.last_id,
            view_type: "line",
            osm_data: null,
            osmDataSource: null,
            selected_tags_for_label: savedStyles?.selected_tags_for_label,
            label_visible: savedStyles?.label_visible,
            label_text_size: savedStyles?.label_text_size,
            label_offset: savedStyles?.label_offset,
            label_text_color: savedStyles?.label_text_color,
            label_interval: savedStyles?.label_interval,
            style_invisible_default: !!savedStyles?.style_invisible_default,
            style_stroke_width: savedStyles?.style_stroke_width ?? geojsonOption?.defaultLineWidth ?? 2,
            style_fill_color:
              (applyColorChange === true && projectTypeLoading !== "main" ? shiftHexColor(savedStyles?.style_fill_color) : savedStyles?.style_fill_color) ??
              geojsonOption?.defaultFillColor ??
              "#ffffff",

            style_outline_color:
              (applyColorChange === true && projectTypeLoading !== "main" ? shiftHexColor(savedStyles?.style_outline_color) : savedStyles?.style_outline_color) ??
              geojsonOption?.defaultOutlineColor ??
              "#ffffff",
            style_type: savedStyles?.style_type ?? geojsonOption?.defaultStyle ?? "solid",
            style_pattern: savedStyles?.style_pattern ?? geojsonOption?.defaultStyle ?? "1111111111111111",
            style_fill_pattern: savedStyles?.style_fill_pattern ?? geojsonOption?.defaultPattren ?? "",
            is_center_lane: false,
            auto_select: false,
            show_lanelet: false,
            is_edited: isEdited,
            layer_id: serverLayerId,
            url: url ? url : isUrl ? fileName : "",
            loading: true,
            priority: Number.isFinite(Number(geojsonOption?.priority)) ? Number(geojsonOption.priority) : 0,
            _priorityOrder: typeof performance !== "undefined" ? performance.now() : Date.now(),
            geojson_type: getGeometryTypeGeojson(cleanedGeojsonData),
            project_id: projectId || selectedProject?.id,
            is_load_from_database: isLoadFromDatabase,
            sql_schema: sqlSchema,
            sql_table: sqlTable,
            sql_db: sqlDB,
            sql_geometry_field: sqlGeometryField,
            sql_where: sqlWhere,
            isDisableLoading,
            isFindBreakPoints: false,
            FindClosePoints: false,
            filters: savedStyles?.filters ? savedStyles?.filters : [],
          };

          // If auto-save, mark edited before insert (actual save happens later)
          if (shouldAutoSave) {
            newMapObject.project_id = projectId;
            newMapObject.is_edited = true;
          }

          setMapObjects((prev) => insertByPriority(prev, newMapObject));
        } else {
          // update existing mapObject (main)
          setMapObjects((prev) => {
            const arr = [...prev];
            const idx = arr.findIndex((o) => o.id === id);

            if (idx === -1) return arr;

            const current = arr[idx];

            // remove old datasource from viewer
            if (current.dataSource && viewerRef?.current?.dataSources?.remove) {
              try {
                viewerRef.current.dataSources.remove(current.dataSource);
              } catch (_) {}
            }

            let updatedObj = {
              ...current,
              geojson: cleanedGeojsonData,
              original_geojson: cleanedGeojsonData,
              hiddenGeojson,
              ...bboxLayerData,
              geojson_table_columns: cleanedGeojsonData?.table_columns,
              geojson_geometry_type: cleanedGeojsonData?.geometry_type,
              geojson_last_id: cleanedGeojsonData?.last_id,
              geojson_type: getGeometryTypeGeojson(cleanedGeojsonData),
              isOverlayText: typeof isOverlayTextGeojson === "boolean" ? isOverlayTextGeojson : current.isOverlayText,
              is_edited: isEdited,
              layer_id: serverLayerId ?? current.layer_id,
              url: url ? url : isUrl && fileName ? fileName : current.url,
              loading: true,
              dataSource: null,
              project_id: projectId ?? current.project_id,
              is_load_from_database: isLoadFromDatabase ?? current.is_load_from_database,
              sql_schema: sqlSchema ?? current.sql_schema,
              sql_table: sqlTable ?? current.sql_table,
              sql_db: sqlDB ?? current.sql_db,
              sql_geometry_field: sqlGeometryField ?? current.sql_geometry_field,
              sql_where: sqlWhere ?? current.sql_where,
              isDisableLoading: typeof isDisableLoading === "boolean" ? isDisableLoading : current.isDisableLoading,
            };

            if (savedStyles) {
              updatedObj = {
                ...updatedObj,
                selected_tags_for_label: savedStyles.selected_tags_for_label ?? updatedObj.selected_tags_for_label,
                label_visible: savedStyles.label_visible ?? updatedObj.label_visible,
                label_text_size: savedStyles.label_text_size ?? updatedObj.label_text_size,
                label_offset: savedStyles.label_offset ?? updatedObj.label_offset,
                label_text_color: savedStyles.label_text_color ?? updatedObj.label_text_color,
                label_interval: savedStyles.label_interval ?? updatedObj.label_interval,
                style_invisible_default: typeof savedStyles.style_invisible_default === "boolean" ? savedStyles.style_invisible_default : updatedObj.style_invisible_default,
                style_stroke_width: savedStyles.style_stroke_width ?? updatedObj.style_stroke_width,
                style_fill_color:
                  (applyColorChange === true && projectTypeLoading !== "main" ? shiftHexColor(savedStyles.style_fill_color) : savedStyles.style_fill_color) ?? updatedObj.style_fill_color,

                style_outline_color:
                  (applyColorChange === true && projectTypeLoading !== "main" ? shiftHexColor(savedStyles.style_outline_color) : savedStyles.style_outline_color) ?? updatedObj.style_outline_color,

                style_type: savedStyles.style_type ?? updatedObj.style_type,
                style_pattern: savedStyles.style_pattern ?? updatedObj.style_pattern,
                style_fill_pattern: savedStyles.style_fill_pattern ?? updatedObj.style_fill_pattern,
              };
            }

            arr[idx] = updatedObj;
            return arr;
          });
        }
      } else if (projectTypeLoading === "assest") {
        if (!isUpdateExisting) {
          // create new mapObject in "assest"
          setMapObjectsAssest((prev) => {
            const newMapObject = {
              id,
              name: "Geojson",
              entities: [],
              type,
              file_name: fileName,
              file_type: ".geojson",
              inner_entities: [],
              visible: savedStyles?.style_invisible_default !== true,
              dataSource: null,
              label_visible: false,
              geojson: cleanedGeojsonData,
              isOverlayText: isOverlayTextGeojson,
              original_geojson: cleanedGeojsonData,
              hiddenGeojson,
              ...bboxLayerData,
              geojson_table_columns: cleanedGeojsonData?.table_columns,
              geojson_geometry_type: cleanedGeojsonData?.geometry_type,
              geojson_last_id: cleanedGeojsonData?.last_id,
              view_type: "line",
              osm_data: null,
              osmDataSource: null,
              selected_tags_for_label: savedStyles?.selected_tags_for_label,
              label_visible: savedStyles?.label_visible,
              label_text_size: savedStyles?.label_text_size,
              label_offset: savedStyles?.label_offset,
              label_text_color: savedStyles?.label_text_color,
              label_interval: savedStyles?.label_interval,
              style_invisible_default: !!savedStyles?.style_invisible_default,
              style_stroke_width: savedStyles?.style_stroke_width ?? geojsonOption?.defaultLineWidth ?? 2,
              style_fill_color:
                (applyColorChange === true && projectTypeLoading !== "main" ? shiftHexColor(savedStyles?.style_fill_color) : savedStyles?.style_fill_color) ??
                geojsonOption?.defaultFillColor ??
                "#ffffff",

              style_outline_color:
                (applyColorChange === true && projectTypeLoading !== "main" ? shiftHexColor(savedStyles?.style_outline_color) : savedStyles?.style_outline_color) ??
                geojsonOption?.defaultOutlineColor ??
                "#ffffff",
              style_type: savedStyles?.style_type ?? geojsonOption?.defaultStyle ?? "solid",
              style_pattern: savedStyles?.style_pattern ?? geojsonOption?.defaultStyle ?? "1111111111111111",
              style_fill_pattern: savedStyles?.style_fill_pattern ?? geojsonOption?.defaultPattren ?? "",
              is_center_lane: false,
              auto_select: false,
              show_lanelet: false,
              is_edited: isEdited,
              layer_id: serverLayerId,
              url: isUrl ? fileName : "",
              loading: true,
              priority: Number.isFinite(Number(geojsonOption?.priority)) ? Number(geojsonOption.priority) : 0,
              _priorityOrder: typeof performance !== "undefined" ? performance.now() : Date.now(),
              geojson_type: getGeometryTypeGeojson(cleanedGeojsonData),
              project_id: projectId,
              is_load_from_database: isLoadFromDatabase,
              sql_schema: sqlSchema,
              sql_table: sqlTable,
              sql_where: sqlWhere,
            };
            return insertByPriority(prev, newMapObject);
          });
        } else {
          // update existing mapObject in "assest"
          setMapObjectsAssest((prev) => {
            const next = [...prev];
            const idx = next.findIndex((o) => o.id === id);
            if (idx !== -1) {
              const current = next[idx];

              if (current.dataSource && viewerRef?.current?.dataSources?.remove) {
                try {
                  viewerRef.current.dataSources.remove(current.dataSource);
                } catch (err) {}
              }

              let updatedObj = {
                ...current,
                geojson: cleanedGeojsonData,
                original_geojson: cleanedGeojsonData,
                hiddenGeojson,
                ...bboxLayerData,
                geojson_table_columns: cleanedGeojsonData?.table_columns,
                geojson_geometry_type: cleanedGeojsonData?.geometry_type,
                geojson_last_id: cleanedGeojsonData?.last_id,
                geojson_type: getGeometryTypeGeojson(cleanedGeojsonData),
                isOverlayText: typeof isOverlayTextGeojson === "boolean" ? isOverlayTextGeojson : current.isOverlayText,
                is_edited: isEdited,
                layer_id: serverLayerId ?? current.layer_id,
                url: isUrl && fileName ? fileName : current.url,
                loading: true,
                dataSource: null,
                project_id: projectId ?? current.project_id,
                is_load_from_database: isLoadFromDatabase ?? current.is_load_from_database,
                sql_schema: sqlSchema ?? current.sql_schema,
                sql_table: sqlTable ?? current.sql_table,
                sql_where: sqlWhere ?? current.sql_where,
              };

              if (savedStyles) {
                updatedObj = {
                  ...updatedObj,
                  selected_tags_for_label: savedStyles.selected_tags_for_label ?? updatedObj.selected_tags_for_label,
                  label_visible: savedStyles.label_visible ?? updatedObj.label_visible,
                  label_text_size: savedStyles.label_text_size ?? updatedObj.label_text_size,
                  label_offset: savedStyles.label_offset ?? updatedObj.label_offset,
                  label_text_color: savedStyles.label_text_color ?? updatedObj.label_text_color,
                  label_interval: savedStyles.label_interval ?? updatedObj.label_interval,
                  style_invisible_default: typeof savedStyles.style_invisible_default === "boolean" ? savedStyles.style_invisible_default : updatedObj.style_invisible_default,
                  style_stroke_width: savedStyles.style_stroke_width ?? updatedObj.style_stroke_width,
                  style_fill_color:
                    (applyColorChange === true && projectTypeLoading !== "main" ? shiftHexColor(savedStyles.style_fill_color) : savedStyles.style_fill_color) ?? updatedObj.style_fill_color,

                  style_outline_color:
                    (applyColorChange === true && projectTypeLoading !== "main" ? shiftHexColor(savedStyles.style_outline_color) : savedStyles.style_outline_color) ?? updatedObj.style_outline_color,

                  style_type: savedStyles.style_type ?? updatedObj.style_type,
                  style_pattern: savedStyles.style_pattern ?? updatedObj.style_pattern,
                  style_fill_pattern: savedStyles.style_fill_pattern ?? updatedObj.style_fill_pattern,
                };
              }

              next[idx] = updatedObj;
            }
            return next;
          });
        }
      }

      // merge saved style overrides for Cesium options

      const tempStyle = {};

      const shouldShiftColors = applyColorChange === true && projectTypeLoading !== "main";

      const strokeColor = shouldShiftColors ? shiftHexColor(savedStyles?.style_fill_color) : savedStyles?.style_fill_color;
      const outlineColor = shouldShiftColors ? shiftHexColor(savedStyles?.style_outline_color) : savedStyles?.style_outline_color;

      if (savedStyles?.style_stroke_width) tempStyle.defaultLineWidth = savedStyles?.style_stroke_width;
      if (strokeColor) tempStyle.defaultFillColor = strokeColor;
      if (outlineColor) tempStyle.defaultOutlineColor = outlineColor;
      if (savedStyles?.style_type) tempStyle.defaultStyle = savedStyles?.style_type;
      if (savedStyles?.style_pattern) tempStyle.dottedDashLength = savedStyles?.style_pattern;

      // overlay text loader
      if (isOverlayTextGeojson && projectTypeLoading === "main") {
        await loadOverlayTextGeoJSONToCesium({
          originalCameraControls,
          viewer: viewerRef.current,
          geojson: cleanedGeojsonData,
          options: {
            ...defaultGeojsonStyle,
            ...geojsonOption,
            ...tempStyle,
            zoomToDataSource,
          },
          layerName: fileName || "",
          id,
          invisible: savedStyles?.style_invisible_default === true,
          callback: (dataSource, geoJson, entities, styleFillColor, styleOutsideColor, styleStrokeWidth, styleType) => {
            const optionsObj = {
              ...defaultGeojsonStyle,
              ...geojsonOption,
              ...tempStyle,
            };

            setMapObjects((prev) =>
              prev.map((o) =>
                o.id === id
                  ? {
                      ...o,
                      dataSource,
                      billboards: entities,
                      style_stroke_width: styleStrokeWidth,
                      style_fill_color: styleFillColor,
                      style_outline_color: styleOutsideColor,
                      style_type: styleType,
                      loading: false,
                      options: optionsObj,
                    }
                  : o,
              ),
            );

            // ✅ ADD: apply label only if savedStyles exists and label_visible === true
            maybeApplySavedLabel(dataSource);
            if (savedStyles?.filters) {
              applyGeojsonLayerFilters({
                viewer: viewerRef.current,
                dataSource,
                filters: savedStyles?.filters || [],
                layerId: id,
                setMapObjects,
                closeDialog: false,
              });
            }

            if (shouldAutoSave) {
              const layerSnapshotForSave = {
                id,
                name: "Geojson",
                entities: [],
                type,
                file_name: isUrl && projectId && selectedProject ? extractBaseName(fileName) : fileName,
                file_type: ".geojson",
                inner_entities: [],
                visible: isDisableLoading ? false : savedStyles?.style_invisible_default !== true,
                dataSource,
                label_visible: false,
                geojson: cleanedGeojsonData,
                isOverlayText: true,
                original_geojson: cleanedGeojsonData,
                hiddenGeojson,
                ...bboxLayerData,
                geojson_table_columns: cleanedGeojsonData?.table_columns,
                geojson_geometry_type: cleanedGeojsonData?.geometry_type,
                geojson_last_id: cleanedGeojsonData?.last_id,
                view_type: "line",
                osm_data: null,
                osmDataSource: null,
                selected_tags_for_label: savedStyles?.selected_tags_for_label,
                label_visible: savedStyles?.label_visible,
                label_text_size: savedStyles?.label_text_size,
                label_offset: savedStyles?.label_offset,
                label_text_color: savedStyles?.label_text_color,
                label_interval: savedStyles?.label_interval,
                style_invisible_default: !!savedStyles?.style_invisible_default,
                style_stroke_width: styleStrokeWidth,
                style_fill_color: styleFillColor,
                style_outline_color: styleOutsideColor,
                style_type: styleType,
                style_pattern: savedStyles?.style_pattern ?? geojsonOption?.defaultStyle ?? "1111111111111111",
                style_fill_pattern: savedStyles?.style_fill_pattern ?? geojsonOption?.defaultPattren ?? "",
                is_center_lane: false,
                auto_select: false,
                show_lanelet: false,
                is_edited: true,
                layer_id: serverLayerId,
                url: url ? url : isUrl ? fileName : "",
                loading: false,
                priority: Number.isFinite(Number(geojsonOption?.priority)) ? Number(geojsonOption.priority) : 0,
                _priorityOrder: typeof performance !== "undefined" ? performance.now() : Date.now(),
                geojson_type: getGeometryTypeGeojson(cleanedGeojsonData),
                project_id: projectId,
                is_load_from_database: isLoadFromDatabase,
                sql_schema: sqlSchema,
                sql_table: sqlTable,
                sql_db: sqlDB,
                sql_geometry_field: sqlGeometryField,
                sql_where: sqlWhere,
                isDisableLoading,
                isFindBreakPoints: false,
                FindClosePoints: false,
                billboards: entities,
                options: optionsObj,
              };

              maybeAutoSaveToServer(layerSnapshotForSave);
            }
          },
          setMapObjects,
        });

        if (loadingDataForLayers) {
          let v = true;
          if (loadingDataForLayers.current.current + 1 === loadingDataForLayers.current.total) v = false;
          loadingDataForLayers.current = {
            current: loadingDataForLayers.current.current + 1,
            total: loadingDataForLayers.current.total,
            message: loadingDataForLayers.current?.message,
            show: v,
          };
        }

        return resolve();
      }

      // normal GeoJSON load
      loadGeoJSONToCesium({
        viewer: viewerRef.current,
        geojson: cleanedGeojsonData,
        options: {
          ...defaultGeojsonStyle,
          ...geojsonOption,
          ...tempStyle,
          zoomToDataSource,
        },
        callback: (dataSource, geoJson, billboards, styleFillColor, styleOutsideColor, styleStrokeWidth, styleType) => {
          const optionsObj = {
            ...defaultGeojsonStyle,
            ...geojsonOption,
            ...tempStyle,
          };

          if (projectTypeLoading === "main") {
            setMapObjects((prev) =>
              prev.map((o) =>
                o.id === id
                  ? {
                      ...o,
                      dataSource,
                      billboards,
                      style_stroke_width: styleStrokeWidth,
                      style_fill_color: styleFillColor,
                      style_outline_color: styleOutsideColor,
                      style_type: styleType,
                      loading: false,
                      options: optionsObj,
                    }
                  : o,
              ),
            );

            // ✅ ADD: apply label only if savedStyles exists and label_visible === true
            maybeApplySavedLabel(dataSource);
            if (savedStyles?.filters) {
              applyGeojsonLayerFilters({
                viewer: viewerRef.current,
                dataSource,
                filters: savedStyles?.filters || [],
                layerId: id,
                setMapObjects,
                closeDialog: false,
              });
            }
            if (shouldAutoSave) {
              const layerSnapshotForSave = {
                id,
                name: "Geojson",
                entities: [],
                type,
                file_name: isUrl && projectId && selectedProject ? extractBaseName(fileName) : fileName,
                file_type: ".geojson",
                inner_entities: [],
                visible: isDisableLoading ? false : savedStyles?.style_invisible_default !== true,
                dataSource,
                label_visible: false,
                geojson: cleanedGeojsonData,
                isOverlayText: false,
                original_geojson: cleanedGeojsonData,
                hiddenGeojson,
                ...bboxLayerData,
                geojson_table_columns: cleanedGeojsonData?.table_columns,
                geojson_geometry_type: cleanedGeojsonData?.geometry_type,
                geojson_last_id: cleanedGeojsonData?.last_id,
                view_type: "line",
                osm_data: null,
                osmDataSource: null,
                selected_tags_for_label: savedStyles?.selected_tags_for_label,
                label_visible: savedStyles?.label_visible,
                label_text_size: savedStyles?.label_text_size,
                label_offset: savedStyles?.label_offset,
                label_text_color: savedStyles?.label_text_color,
                label_interval: savedStyles?.label_interval,
                style_invisible_default: !!savedStyles?.style_invisible_default,
                style_stroke_width: styleStrokeWidth,
                style_fill_color: styleFillColor,
                style_outline_color: styleOutsideColor,
                style_type: styleType,
                style_pattern: savedStyles?.style_pattern ?? geojsonOption?.defaultStyle ?? "1111111111111111",
                style_fill_pattern: savedStyles?.style_fill_pattern ?? geojsonOption?.defaultPattren ?? "",
                is_center_lane: false,
                auto_select: false,
                show_lanelet: false,
                is_edited: true,
                layer_id: serverLayerId,
                url: url ? url : isUrl ? fileName : "",
                loading: false,
                priority: Number.isFinite(Number(geojsonOption?.priority)) ? Number(geojsonOption.priority) : 0,
                _priorityOrder: typeof performance !== "undefined" ? performance.now() : Date.now(),
                geojson_type: getGeometryTypeGeojson(cleanedGeojsonData),
                project_id: projectId,
                is_load_from_database: isLoadFromDatabase,
                sql_schema: sqlSchema,
                sql_table: sqlTable,
                sql_db: sqlDB,
                sql_geometry_field: sqlGeometryField,
                sql_where: sqlWhere,
                isDisableLoading,
                isFindBreakPoints: false,
                FindClosePoints: false,
                billboards,
                options: optionsObj,
              };

              maybeAutoSaveToServer(layerSnapshotForSave);
            }

            if (onLoaded != null && typeof onLoaded == "function") onLoaded();
          } else if (projectTypeLoading === "assest") {
            setMapObjectsAssest((prevState) => {
              const updated = [...prevState];
              const idx = updated.findIndex((obj) => obj.id === id);
              if (idx !== -1) {
                updated[idx] = {
                  ...updated[idx],
                  dataSource,
                  billboards,
                  style_stroke_width: styleStrokeWidth,
                  style_fill_color: styleFillColor,
                  style_outline_color: styleOutsideColor,
                  style_type: styleType,
                  loading: false,
                  options: optionsObj,
                };
              }
              return updated;
            });
          }
        },
        geojsonType: type,
        layerName: fileName || "",
        id,
        invisible: savedStyles?.style_invisible_default === true,
        labelOptions: {
          selected_tags_for_label: savedStyles?.selected_tags_for_label,
          label_text_size: savedStyles?.label_text_size,
          label_visible: savedStyles?.label_visible,
          label_offset: savedStyles?.label_offset,
          label_text_color: savedStyles?.label_text_color,
          label_interval: savedStyles?.label_interval,
        },
      });

      if (loadingDataForLayers) {
        let v = true;
        if (loadingDataForLayers.current.current + 1 === loadingDataForLayers.current.total) v = false;
        loadingDataForLayers.current = {
          current: loadingDataForLayers.current.current + 1,
          total: loadingDataForLayers.current.total,
          message: loadingDataForLayers.current?.message,
          show: v,
        };
      }

      resolve();
    } catch (e) {
      reject(e);
    }
  });
}
